import {
  OrganizationService,
  OrganizationVM,
} from "@propertelligent/client-api";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import EditorToolBar from "../../common/EditorToolBar";
import Card from "../../../newComponents/card";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import InputField from "../../../newComponents/form-fields/InputField";
import SelectField from "../../../newComponents/form-fields/SelectField";
import apiHelper from "../../../utils/apiHelper";
import {
  convertDatetimeLocalInputValueToUTC,
  convertUTCToDatetimeLocalInputValue,
} from "../../../utils/DateHelper";
import {
  MaintenanceBannerEntityTypesEnum,
  MaintenanceBannerService,
  MaintenanceBannerVM,
} from "../../../services/maintenanceBanner.service";
import "../platform-portal.css";

const formId = "maintenance-banner-form";
const END_TIME_VALIDATION_MESSAGE = "End time must be after start time.";
const TARGET_OPTIONS = {
  allUsers: "allUsers",
  specificOrganization: "specificOrganization",
} as const;

interface MaintenanceBannerDrawerProps {
  isOpen: boolean;
  banner: MaintenanceBannerVM | null;
  onClose: () => void;
  onSaved: () => void;
}

interface MaintenanceBannerFormValues {
  title: string;
  messageBody: string;
  startTimeLocal: string;
  endTimeLocal: string;
  targetType: (typeof TARGET_OPTIONS)[keyof typeof TARGET_OPTIONS];
  organizationId: string;
}

const hasMeaningfulHtml = (html: string) => {
  if (!html) {
    return false;
  }

  const scratch = document.createElement("div");
  scratch.innerHTML = html;
  const hasText = (scratch.textContent || "")
    .replace(/\u00a0/g, " ")
    .trim().length > 0;

  return hasText || Boolean(scratch.querySelector("img"));
};

const toDefaultValues = (
  banner: MaintenanceBannerVM | null,
): MaintenanceBannerFormValues => ({
  title: banner?.title ?? "",
  messageBody: banner?.messageBody ?? "",
  startTimeLocal: convertUTCToDatetimeLocalInputValue(banner?.startTimeUtc),
  endTimeLocal: convertUTCToDatetimeLocalInputValue(banner?.endTimeUtc),
  targetType:
    banner?.entityId != null
      ? TARGET_OPTIONS.specificOrganization
      : TARGET_OPTIONS.allUsers,
  organizationId:
    banner?.entityId != null ? String(banner.entityId) : "",
});

const MaintenanceBannerDrawer = ({
  isOpen,
  banner,
  onClose,
  onSaved,
}: MaintenanceBannerDrawerProps) => {
  const [organizations, setOrganizations] = useState<OrganizationVM[]>([]);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const isEditMode = Boolean(banner?.id);

  const methods = useForm<MaintenanceBannerFormValues>({
    mode: "onChange",
    defaultValues: toDefaultValues(banner),
  });

  const {
    clearErrors,
    formState: { errors, isDirty, isSubmitting, isValid },
    getValues,
    register,
    reset,
    setError,
    setValue,
    trigger,
    unregister,
    watch,
  } = methods;

  const targetType = watch("targetType");
  const startTimeLocal = watch("startTimeLocal");
  const organizationOptions = useMemo(
    () =>
      organizations.map((organization) => ({
        Id: Number(organization.id),
        Name: organization.name || `Organization ${organization.id}`,
      })),
    [organizations],
  );

  useEffect(() => {
    register("messageBody", {
      validate: (value) =>
        hasMeaningfulHtml(value) || "Message body is required",
    });

    return () => unregister("messageBody");
  }, [register, unregister]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const defaults = toDefaultValues(banner);
    reset(defaults);

    if (bodyRef.current) {
      bodyRef.current.innerHTML = defaults.messageBody;
    }
  }, [banner, isOpen, reset]);

  useEffect(() => {
    trigger("endTimeLocal");
  }, [startTimeLocal, trigger]);

  useEffect(() => {
    if (targetType !== TARGET_OPTIONS.specificOrganization) {
      if (getValues("organizationId")) {
        setValue("organizationId", "", {
          shouldDirty: true,
          shouldValidate: false,
        });
      }

      clearErrors("organizationId");
      unregister("organizationId");
    }
  }, [clearErrors, getValues, setValue, targetType, unregister]);

  useEffect(() => {
    if (
      !isOpen ||
      targetType !== TARGET_OPTIONS.specificOrganization ||
      organizations.length > 0
    ) {
      return;
    }

    let isCancelled = false;

    (async () => {
      const response = await apiHelper(
        OrganizationService.getApiOrganization,
        { showNotification: false },
      );

      if (isCancelled) {
        return;
      }

      const success = response?.data?.success;
      const organizationList = Array.isArray(success)
        ? success
        : success
          ? [success]
          : [];

      setOrganizations(organizationList);
    })();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, organizations.length, targetType]);

  const handleMessageBodyInput = () => {
    setValue("messageBody", bodyRef.current?.innerHTML ?? "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSubmit = async (values: MaintenanceBannerFormValues) => {
    const messageBody = bodyRef.current?.innerHTML ?? "";

    setValue("messageBody", messageBody, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (!hasMeaningfulHtml(messageBody)) {
      setError("messageBody", {
        type: "required",
        message: "Message body is required",
      });
      return;
    }

    clearErrors("messageBody");

    const bodyIsValid = await trigger("messageBody");
    if (!bodyIsValid) {
      return;
    }

    const isSpecificOrganization =
      values.targetType === TARGET_OPTIONS.specificOrganization;

    const payload = {
      id: banner?.id ?? 0,
      title: values.title.trim(),
      messageBody,
      startTimeUtc: convertDatetimeLocalInputValueToUTC(values.startTimeLocal),
      endTimeUtc: convertDatetimeLocalInputValueToUTC(values.endTimeLocal),
      entityTypeId: isSpecificOrganization
        ? MaintenanceBannerEntityTypesEnum.Organization
        : null,
      entityId: isSpecificOrganization ? Number(values.organizationId) : null,
    };

    const response = await apiHelper(
      isEditMode
        ? MaintenanceBannerService.putApiMaintenanceBanner
        : MaintenanceBannerService.postApiMaintenanceBanner,
      {
        showNotification: true,
        successMessage: isEditMode
          ? "Maintenance banner updated"
          : "Maintenance banner created",
        failureMessage: isEditMode
          ? "Unable to update maintenance banner"
          : "Unable to create maintenance banner",
      },
      payload,
    );

    if (response?.data?.success) {
      onSaved();
      onClose();
      return;
    }

    const fieldMap: Record<string, keyof MaintenanceBannerFormValues> = {
      Title: "title",
      MessageBody: "messageBody",
      StartTimeUtc: "startTimeLocal",
      EndTimeUtc: "endTimeLocal",
      EntityId: "organizationId",
    };

    Object.entries(response?.errors ?? {}).forEach(([key, messages]) => {
      const fieldName = fieldMap[key];

      if (!fieldName || !Array.isArray(messages) || messages.length === 0) {
        return;
      }

      setError(fieldName, {
        type: "server",
        message: messages[0],
      });
    });
  };

  return (
    <RightSideModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Maintenance Banner" : "Add Maintenance Banner"}
      buttonlabel={isSubmitting ? "Saving..." : "Save"}
      formId={formId}
      maxWidth={680}
      isDirty={isDirty}
      isLoading={isSubmitting}
      isSubmitDisabled={isSubmitting || !isDirty || !isValid}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id={formId} onSubmit={methods.handleSubmit(handleSubmit)}>
            <div className="row">
              <div className="col-12 mb-1">
                <InputField
                  name="title"
                  label="Title"
                  placeholder="Enter title"
                  required
                  requiredMessage="Title is required"
                  allowSpecialChars
                />
              </div>

              <div className="col-12 mb-3">
                <label className="sub_heading black">
                  Message Body <span className="text-danger">*</span>
                </label>
                <div className="maintenance-banner-editor mt-2">
                  <EditorToolBar
                    editorRef={bodyRef}
                    handleFileChange={() => undefined}
                    disableAttachments={true}
                  />
                  <div
                    ref={bodyRef}
                    className={`maintenance-banner-editor-body ${
                      errors.messageBody ? "is-invalid" : ""
                    }`}
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    onInput={handleMessageBodyInput}
                    onBlur={() => trigger("messageBody")}
                  />
                </div>
                {errors.messageBody?.message && (
                  <div className="invalid-feedback d-block">
                    {String(errors.messageBody.message)}
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="startTimeLocal"
                  label="Start Date/Time"
                  type="datetime-local"
                />
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="endTimeLocal"
                  label="End Date/Time"
                  type="datetime-local"
                  validation={{
                    validate: (value: string) => {
                      const startTimeLocal = getValues("startTimeLocal");

                      if (!startTimeLocal || !value) {
                        return true;
                      }

                      return (
                        new Date(value).getTime() >
                          new Date(startTimeLocal).getTime() ||
                        END_TIME_VALIDATION_MESSAGE
                      );
                    },
                  }}
                />
              </div>

              <div className="col-md-6 mb-1">
                <SelectField
                  name="targetType"
                  label="Applies To"
                  options={[
                    { Id: TARGET_OPTIONS.allUsers, Name: "All Users" },
                    {
                      Id: TARGET_OPTIONS.specificOrganization,
                      Name: "Specific Organization",
                    },
                  ]}
                  placeholderText="Select audience"
                  required
                  requiredMessage="Applies To is required"
                />
              </div>

              {targetType === TARGET_OPTIONS.specificOrganization && (
                <div className="col-md-6 mb-1">
                  <SelectField
                    name="organizationId"
                    label="Organization"
                    options={organizationOptions}
                    placeholderText="Select organization"
                    required
                    requiredMessage="Organization is required"
                  />
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default MaintenanceBannerDrawer;
